<!-- Marker edit form -->
<template>
<div class="marker-edit">
  <div class="row">
    <div class="col-md-12">
      <h2 v-if="$route.meta.existingMarker" class="feature">{{ $t("marker.edit") }}</h2>
      <h2 v-else class="feature">{{ $t("marker.create") }}</h2>
    </div>
  </div>

  <!-- Marker name input -->
  <form @submit.prevent="submit" @keydown.enter.stop.prevent>
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label>{{ $t("marker.name") }}</label>
          <input-localized-textarea
            v-model="marker.name"
            :locales="locales"
            :current-locale="currentEditLocale"
            :attrs="{
              placeholder: $t('marker.placeholder.name'),
              maxlength: 32,
              name: 'name',
              required: true,
              rows: 1,
              autofocus: true,
              disabled: disabled
            }">
          </input-localized-textarea>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <!-- Marker description input -->
        <div class="form-group">
          <label>{{ $t("marker.description") }}</label>
          <input-localized-textarea
            v-model="marker.description"
            :locales="locales"
            :current-locale="currentEditLocale"
            :attrs="{
              placeholder: $t('marker.placeholder.description'),
              maxlength: 55,
              name: 'description',
              required: true,
              rows: 2,
              autofocus: false,
              disabled: disabled
            }">
          </input-localized-textarea>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <!-- Marker description input -->
        <div class="form-group">
          <label>{{ $t("marker.long_description") }}</label>
          <input-localized-textarea
            v-model="marker.long_description"
            :locales="locales"
            :current-locale="currentEditLocale"
            :attrs="{
              placeholder: $t('marker.placeholder.long_description'),
              maxlength: 600,
              name: 'long_description',
              required: true,
              rows: 3,
              autofocus: false,
              disabled: disabled
            }">
          </input-localized-textarea>
        </div>
      </div>
    </div>

    <div class="locale-selection">
      <a
        v-for="l in locales"
        :class="{'selected': l === currentEditLocale}"
        @click.stop.prevent="currentEditLocale = l"
        href="">
          <span v-if='l == "en"'>{{l}} (default)</span>
          <span v-else>{{l}}</span>
      </a>
    </div>

    <!-- marker ml keyword input -->
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label for="ml_keyword">
            {{ $t('marker.placeholder.ml_keyword') }}
          </label>
          <input
            class="form-control"
            type="text"
            name="ml_keyword"
            id="ml_keyword"
            v-model="marker.ml_keyword"
            :disabled="disabled"
          />
        </div>
      </div>
    </div>

    <!-- marker user group selection -->
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label for="ml_keyword">
            {{ $t('marker.targeted_user_groups') }}
          </label>
          <group-selector v-model="marker.targeted_groups" :groups="groups" v-if="groups">
          </group-selector>
        </div>
      </div>
    </div>

    <!-- Marker brand selection -->
    <!-- Only shown if there are more than 1 selectable brand -->
    <div class="row" v-show="brands.length > 1">
      <div class="col-md-12">
        <div class="form-group">
          <brand-selector class="brand-selection"
              :brands="brands"
              v-model="marker.brand"
            ></brand-selector>
        </div>
      </div>
    </div>

    <div class="row flex-container flex-stretch flex-reverse-wrap">
      <div class="col-md-7 col-xs-12" id="location-time-container">

        <!-- Event location fieldset -->
        <fieldset>
          <legend>{{ $t('marker.location') }} <small class="smaller"><a href="" @click.stop.prevent="edit = true">{{ $t('marker.edit_on_map') }}</a></small></legend>

          <!-- Marker address and radius inputs-->
          <div class="row">
            <div class="col-md-8">
              <input-places
                @place-selected="updateMarkerPosition"
                :position="position"
                :attrs="{
                  id: 'input-places',
                  placeholder: $t('marker.placeholder.address'),
                  disabled: disabled
                }">
                <label slot="before" for="input-places">
                  {{ $t('marker.address') }} <small class="optional">{{ $t("optional") }}</small>
                </label>
              </input-places>
            </div>
            <div class="form-group col-md-4">
              <label for="radius">
                {{ $t("marker.radius") }} <small>({{ $t("marker.meters") }})</small>
              </label>
              <input-number id="radius"
                v-model="marker.radius"
                increment-by="100"
                :max-value="maxRadius"
                min-value="50"
                :disabled="disabled">
              </input-number>
            </div>
          </div>
        </fieldset>

        <!-- Event time fieldset -->
        <fieldset>
          <legend>{{ $t("marker.time") }}</legend>
          <div class="row">
            <!-- Marker start and end time inputs -->
            <div class="form-group col-md-6">
              <label for="startsat">{{ $t("marker.start_date") }}</label>
              <input-datetime v-model="marker.starts_at" :attrs="{
                id: 'startsat',
                placeholder: 'Starts on',
                disabled: disabled}"
              >
              </input-datetime>
            </div>
            <div class="form-group col-md-6">
              <label for="endsat">{{ $t("marker.end_date") }}</label>
              <input-datetime v-model="marker.ends_at" :attrs="{
                id:'endsat',
                placeholder:'Ends on',
                disabled: disabled}">
              </input-datetime>
            </div>
          </div>
        </fieldset>
      </div>

      <!-- Marker Map preview -->
      <div class="col-md-5 flex-container flex-stretch" id="map-preview-container"
        @click.stop.prevent="edit = true">
        <map-preview style="flex: 1; position: relative;" :position="position"></map-preview>
      </div>
    </div>

    <div class="row" v-show="startsInTheFuture">
      <div class="form-group col-md-12">
        <label for="revealAtChoice">{{ $t("marker.reveal_at") }}</label>
        <input-reveal-at v-model="revealAtChoice"></input-reveal-at>
      </div>
    </div>

    <!-- Duration helper:
      Short line of text to remind the user when the marker will activate
      Only shown for new markers, not when editing an existing one.
    -->
    <div class="row" v-if="!existingMarker">
      <duration-helper
        :start="marker.starts_at"
        :end="marker.ends_at"
      >
      </duration-helper>
    </div>
    <fieldset>
      <legend>{{ $t("marker.additional_settings") }}</legend>
      <div>
        <input type="checkbox" id="force-push" name="force-push" v-model="marker.is_push_forced">
        <label for="force-push">Send notifications to all users in selected groups</label>
        <v-tooltip bottom>
          <span slot="activator" class=" glyphicon glyphicon-info-sign"></span>
          <span>indepent of location - if no group is selected, all users will be notified</span>
        </v-tooltip>
      </div>
    </fieldset>
    <div class="row">
      <div class="form-group col-md-3">
        <button type="submit" class="btn btn-primary btn-full-width" :disabled="disabled || loading">{{ $t('save') }}</button>
      </div>
      <div class="col-md-1">
        <div v-show="loading" class="loading-indicator" style="margin-top:16px"></div>
      </div>
    </div>
  </form>

  <!-- Modal map marker location editor -->
  <map-editor v-if="edit" transition="modal"
    @position-changed="updateMarkerPosition"
    @close="edit = false"
    :position="position"
    :max-radius="maxRadius"
    :attrs="{
      'placeholder': $t('marker.placeholder.address_search')
    }">
  </map-editor>

</div>
</template>

<script>
// Main marker edit and create form

import MarkerRepository from '../repositories/MarkerRepository.js'
import events from '../services/events'
import {store, refreshGlobalState} from '../store'

require('leaflet/dist/leaflet.css')
import MapPreview from './markerEdit/MapPreview.vue'
import MapEditor from './markerEdit/MapEditor.vue'

import InputNumber from './generic/InputNumber.vue'
import InputDatetime from './markerEdit/InputDatetime.vue'
import InputLocalizedTextarea from './markerEdit/InputLocalizedText.vue'
import InputPlaces from './markerEdit/InputPlaces.vue'
import BrandSelector from './markerEdit/SelectBrand.vue'
import GroupSelector from './markerEdit/SelectGroups.vue'
import InputRevealAt from './markerEdit/InputRevealAt.vue'

import DurationHelper from './markerEdit/DurationHelper.vue'

import moment from 'moment'

export default {
  components: {
    MapPreview,
    MapEditor,
    InputNumber,
    InputDatetime,
    InputLocalizedTextarea,
    InputPlaces,
    InputRevealAt,
    BrandSelector,
    GroupSelector,
    DurationHelper
  },
  data: function () {
    let data = {
      edit: false, // show map edit modal flag
      loading: false,  // submit loading indicator
      disabled: false, // disabled form inputs while data is loading
      currentEditLocale: 'en',
      locales: ['en', 'fr', 'nl'], // FIXME: set this globally
      maxRadius: 200000,
      revealAtChoice: 'immediate',
      marker: {
        name: {},
        description: {},
        long_description: {},
        position: {
          lat: 0,
          long: 0,
          address: '',
          initial: true
        },
        radius: 1000,
        starts_at: moment().seconds(0),
        ends_at: moment().seconds(0).add(15, 'minutes'),
        brand: null,
        targeted_groups: [],
        ml_keyword: '',
        is_push_forced: false
      }
    }
    data.marker.reveal_at = data.marker.starts_at
    data.locales.forEach(function (l) {
      data.marker.name[l] = ''
      data.marker.description[l] = ''
      data.marker.long_description[l] = ''
    })
    return data
  },
  mounted: function () {
    // refreshing the global state here (in a non-blocking way)
    // to make sure we have the latest list of brands available
    refreshGlobalState(store)
    // when editing an existing marker
    if (this.$route.meta.existingMarker) {
      this.disabled = true
      var markerId = this.$route.params.markerId
      MarkerRepository.fetchMarkerDetails(markerId).then(
        (marker) => {
          // add form-specific attributes to the marker object
          marker.position.address = ''
          marker.position.initial = false
          // detect the reveal_at choice
          if (marker.reveal_at) {
            this.revealAtChoice = Math.abs(marker.reveal_at.diff(marker.starts_at)) <= 60 ? 'eventstart' : 'immediate'
          } else {
            this.revealAtChoice = 'immediate'
          }
          // populate the form
          this.marker = marker
          this.disabled = false
        }
      )
    }
  },
  computed: {
    position: function () {
      return {
        lat: this.marker.position.lat,
        long: this.marker.position.long,
        address: this.marker.position.address,
        radius: this.marker.radius,
        initial: this.marker.position.initial
      }
    },
    brands: function () {
      return store.state.brands ? store.state.brands : []
    },
    groups: function () {
      return store.state.groups ? store.state.groups : []
    },
    duration: function () {
      return this.marker.ends_at - this.marker.starts_at
    },
    startsInTheFuture: function () {
      return this.marker.starts_at.isAfter()
    },
    // return true if we're currently editing an existing marker
    existingMarker: function () {
      return this.$route.meta.existingMarker
    }
  },
  methods: {
    // clean, validate and submit the marker data
    submit: function () {
      const validationResult = this.extraValidation()
      if (!validationResult) {
        return false
      }
      this.loading = true
      this.cleanMarkerData()
      // set the marker team id to the current user team id
      this.marker.team = store.state.teamId
      // set the reveal_at value based on the start_at time
      this.marker.reveal_at = this.revealAtChoice === 'immediate' ? moment().seconds(0) : this.marker.starts_at
      // create or update the marker
      let method
      if (this.$route.meta.existingMarker) {
        method = MarkerRepository.update
      } else {
        method = MarkerRepository.save
      }
      method(this.marker).then(() => {
        this.loading = false
        this.$router.push('/marker')
      }).catch((error) => {
        this.loading = false
        events.emit('error', error, '', {modal: false})
      })
    },
    updateMarkerPosition: function (position) {
      this.marker.position.initial = false
      this.marker.position.lat = position.lat
      this.marker.position.long = position.long
      this.marker.position.address = position.address
      this.marker.radius = position.radius ? position.radius : this.marker.radius
    },
    cleanMarkerData: function () {
      // handle a few marker editing brand-related edge cases
      // where the selected brand is not present in the user's
      // list of available brands
      // FIXME: we should also handle simiar user group (team) related edge cases
      if (this.brands.length === 0 && this.marker.brand !== null) {
        this.marker.brand = null
      }
      const pickedBrand = this.brands.find((brand) => {
        return brand.id === this.marker.brand
      })
      // fallback to a default brand
      if (this.brands.length > 0 && pickedBrand === undefined) {
        this.marker.brand = this.brands[0].id
      }
    },
    // run extra validation that span multiple fields
    extraValidation: function () {
      const validDescriptions = [
        this.marker.name,
        this.marker.description,
        this.marker.long_description
      ].every(this.validateLocalizedField)
      const validDates = this.duration > 0
      return validDescriptions && validDates
    },
    // make sure that localized input fields have a value in at
    // least one language
    validateLocalizedField: function (field) {
      return Object.keys(field).some((val) => {
        return field[val]
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// MarkerEdit.vue?50b9970e